import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface MaintenanceTask {
  id: string
  asset: string
  type: string
  priority: string
  status: string
  due_date: string
  assigned_to: string
  estimated_hours: number
}

interface MaintenanceSchedule {
  week: string
  preventive: number
  corrective: number
  inspection: number
  total_hours: number
  cost_estimate: number
}

interface Statistics {
  total_tasks: number
  tasks_overdue: number
  tasks_completed_mtd: number
  avg_completion_time: number
  mtbf_hours: number
  maintenance_cost_mtd: number
}

export default function MaintenanceManagementPage() {
  const [tasks, setTasks] = useState<MaintenanceTask[]>([])
  const [schedule, setSchedule] = useState<MaintenanceSchedule[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [tasksRes, scheduleRes, statsRes] = await Promise.all([
        fetch('/api/maintenancemanagement/tasks'),
        fetch('/api/maintenancemanagement/schedule'),
        fetch('/api/maintenancemanagement/statistics')
      ])
      if (tasksRes.ok) setTasks(await tasksRes.json())
      if (scheduleRes.ok) setSchedule(await scheduleRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching maintenance management data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getPriorityBadge = (priority: string) => {
    if (priority === 'critical') return 'bg-red-100 text-red-800'
    if (priority === 'high') return 'bg-orange-100 text-orange-800'
    if (priority === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-800'
    if (status === 'in_progress') return 'bg-blue-100 text-blue-800'
    if (status === 'pending_parts') return 'bg-purple-100 text-purple-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Maintenance Management</h1>
          <p className="mt-2 text-gray-600">Phase 256 - Equipment maintenance scheduling</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Tasks</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_tasks}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{statistics.tasks_overdue}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Completed MTD</p>
              <p className="text-2xl font-bold text-green-600">{statistics.tasks_completed_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Time</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_completion_time}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">MTBF</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.mtbf_hours}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Cost MTD</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(statistics.maintenance_cost_mtd)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Maintenance Tasks</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{task.asset}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{task.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(task.priority)}`}>{task.priority}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(task.status)}`}>{task.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{task.due_date}</td>
                    <td className="px-6 py-4 text-gray-500">{task.assigned_to}</td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{task.estimated_hours}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Maintenance Schedule</h2>
          </div>
          <div className="p-6 space-y-4">
            {schedule.map((week, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{week.week}</span>
                  <span className="text-lg font-bold text-green-600">{formatCurrency(week.cost_estimate)}</span>
                </div>
                <div className="grid grid-cols-5 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Preventive</p>
                    <p className="font-medium text-blue-600">{week.preventive}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Corrective</p>
                    <p className="font-medium text-red-600">{week.corrective}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Inspection</p>
                    <p className="font-medium text-purple-600">{week.inspection}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Total Hours</p>
                    <p className="font-medium text-orange-600">{week.total_hours}h</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Cost</p>
                    <p className="font-medium text-green-600">{formatCurrency(week.cost_estimate)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
